import { Navigate, Route, Routes } from 'react-router-dom';
import { Suspense, lazy } from 'react';

import Container from '@modules/common/components/container';
import { FullScreenLoader } from '@modules/common/components/full-screen-loader';
import SkeletonProductGrid from '@modules/skeletons/templates/skeleton-product-grid';
import { DEFAULT_COUNTRY_CODE } from '@lib/constants/country-code';
import { ErrorBoundary } from '../error-boundary';
import AboutPage from '@pages/about';
import ContactPage from '@pages/contact';
import FAQPage from '@pages/faq';
import LatestPage from '@pages/latest';
import { HomePageSkeleton } from '@pages/home/skeleton';
import { AccountPageSkeleton } from '@pages/account/skeleton';
import { CheckoutPageSkeleton } from '@pages/checkout/skeleton';
import { SearchPageSkeleton } from '@pages/search/skeleton';
import ForgetPasswordSentPage from '@pages/account/forget-password-sent';
import ResetPasswordPage from '@pages/account/reset-password';
import AccountOverviewPage from '@pages/account/overview';
import AccountAddressesPage from '@pages/account/addresses';
import AccountOrdersPage from '@pages/account/orders';
import AccountOrderDetailsPage from '@pages/account/order-details';
import AccountPaymentAccountPage from '@pages/account/payment-account';
import AccountCodRefundBalancePage from '@pages/account/cod-refund-balance';
import OrderConfirmedPage from '@pages/order/confirmed';
import { SkeletonOrderConfirmed } from '@pages/order/skeleton';

import MainLayout from './layouts/main-layout';
import AccountLayoutWrapper from './layouts/account-layout-wrapper';
import CheckoutLayout from './layouts/checkout-layout';
import AccountSetupLayout from './layouts/account-setup-layout';
import CountryCodeGuard from './components/country-code-guard';

const HomePage = lazy(() => import('@pages/home'));
const StorePage = lazy(() => import('@pages/store'));
const SearchPage = lazy(() => import('@pages/search'));
const ProductDetailPage = lazy(() => import('@pages/product-detail'));
const CheckoutPage = lazy(() => import('@pages/checkout'));
const AccountSetupCompletePage = lazy(() => import('@pages/account-setup/complete'));

export function AppRoutes() {
  return (
    <Routes>
      {/* Redirect root to default country code */}
      <Route
        path="/"
        element={<Navigate to={`/${DEFAULT_COUNTRY_CODE}`} replace />}
      />

      {/* 驗證 countryCode 是否合法，不合法時 redirect 加上預設 country code 前綴 */}
      <Route path="/:countryCode" element={<CountryCodeGuard />}>
        {/* Main layout routes */}
        <Route element={<Suspense fallback={<FullScreenLoader />}><MainLayout /></Suspense>}>
          {/* Index route - Home page */}
          <Route
            index
            element={
              <ErrorBoundary>
                <Suspense fallback={<HomePageSkeleton />}>
                  <HomePage />
                </Suspense>
              </ErrorBoundary>
            }
          />

          {/* Store pages */}
          <Route
            path="store/*"
            element={
              <ErrorBoundary>
                <Suspense fallback={<SkeletonProductGrid />}>
                  <StorePage />
                </Suspense>
              </ErrorBoundary>
            }
          />

          {/* Search page */}
          <Route
            path="search"
            element={
              <ErrorBoundary>
                <Suspense fallback={<SearchPageSkeleton />}>
                  <SearchPage />
                </Suspense>
              </ErrorBoundary>
            }
          />

          {/* Product detail page */}
          <Route
            path="products/:handle"
            element={
              <ErrorBoundary>
                <Suspense fallback={<SkeletonProductGrid />}>
                  <ProductDetailPage />
                </Suspense>
              </ErrorBoundary>
            }
          />

          {/* 靜態頁面（關於、聯絡、FAQ、最新消息） */}
          <Route path="about" element={<AboutPage />} />
          <Route path="contact" element={<ContactPage />} />
          <Route path="faq" element={<FAQPage />} />
          <Route path="latest" element={<LatestPage />} />

          {/* 公開帳號路由 — 不經過 AccountLayoutWrapper，未登入也能存取 */}
          <Route path="account/reset-password" element={<ErrorBoundary><ResetPasswordPage /></ErrorBoundary>} />
          <Route path="account/forget-password-sent" element={<ErrorBoundary><ForgetPasswordSentPage /></ErrorBoundary>} />

          {/* Account pages - uses AccountLayoutWrapper for auth state */}
          <Route
            path="account"
            element={
              <ErrorBoundary>
                <Suspense fallback={<AccountPageSkeleton />}>
                  <AccountLayoutWrapper />
                </Suspense>
              </ErrorBoundary>
            }
          >
            {/* Dashboard routes (shown when logged in and active) */}
            <Route index element={<AccountOverviewPage />} />
            <Route path="addresses" element={<AccountAddressesPage />} />
            <Route path="orders" element={<AccountOrdersPage />} />
            <Route path="orders/details/:id" element={<AccountOrderDetailsPage />} />
            <Route path="marx-payment-account" element={<AccountPaymentAccountPage />} />
            <Route path="marx-cod-refund-balance" element={<AccountCodRefundBalancePage />} />
            {/* Catch-all redirect to account root */}
            <Route path="*" element={<Navigate to="" replace />} />
          </Route>

          {/* Order confirmed page - outside AccountLayout (no auth wrapper needed, BFF handles auth) */}
          <Route
            path="order/confirmed/:id"
            element={
              <ErrorBoundary>
                <Suspense fallback={<SkeletonOrderConfirmed />}>
                  <OrderConfirmedPage />
                </Suspense>
              </ErrorBoundary>
            }
          />

          {/* 404 fallback */}
          <Route
            path="*"
            element={
              <Container className="py-12">
                <h1 className="text-2xl font-bold">Page Not Found</h1>
              </Container>
            }
          />
        </Route>

        {/* Checkout routes - separate layout from main */}
        <Route path="checkout" element={<Suspense fallback={<FullScreenLoader />}><CheckoutLayout /></Suspense>}>
          <Route
            index
            element={
              <ErrorBoundary>
                <Suspense fallback={<CheckoutPageSkeleton />}>
                  <CheckoutPage />
                </Suspense>
              </ErrorBoundary>
            }
          />
        </Route>

        {/* Account Setup routes - independent layout, no auth required */}
        <Route path="account-setup" element={<AccountSetupLayout />}>
          <Route
            path="complete"
            element={
              <ErrorBoundary>
                <Suspense fallback={<FullScreenLoader />}>
                  <AccountSetupCompletePage />
                </Suspense>
              </ErrorBoundary>
            }
          />
        </Route>
      </Route>
    </Routes>
  );
}
